/**
 * Recognition Service - Manages positive actions/behaviors linked to values
 * Used for the recognition system
 * REFACTORED: Now uses RecognitionRepository with hierarchical collection support
 */

import { RecognitionRepository } from './recognition/repositories/RecognitionRepository';
import { PointsService } from './recognition/core/PointsService';
import { PointsRepository } from './recognition/repositories/PointsRepository';
import { recordAwardForEvent } from './domainAwardResolver';
import { isIssuedRecognition } from './domain/recognitionDefinition';

// RECOGNITION RULES: Reuse discipline infrastructure
import { RuleEvaluator } from './discipline/rules/RuleEvaluator';
import { RuleService } from './discipline/core/RuleService';
import { ConsequenceService } from './discipline/core/ConsequenceService';

// Domain-specific repositories
import { RecognitionRuleRepository } from './recognition/repositories/RecognitionRuleRepository';
import { AchievementRepository } from './recognition/repositories/AchievementRepository';

class RecognitionService {
  constructor() {
    this.repo = new RecognitionRepository();
  }

  /**
   * Create a new recognition
   * @param {Object} recognitionData - Recognition data
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Created recognition
   */
  async createRecognition(recognitionData, schoolId) {
    if (!schoolId) {
      console.warn('RecognitionService.createRecognition: schoolId required but not provided');
      throw new Error('schoolId is required to create recognition');
    }

    try {
      const recognition = {
        ...recognitionData,
        schoolId, // CRITICAL: Must include schoolId for hierarchical collections
        active: true
        // BaseRepository handles createdAt, updatedAt
      };

      return await this.repo.create(recognition);
    } catch (error) {
      console.error('Error creating recognition:', error);
      throw error;
    }
  }

  /**
   * Get all recognitions for a school
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Array of recognitions
   */
  async getRecognitions(schoolId) {
    if (!schoolId) {
      console.warn('RecognitionService.getRecognitions: schoolId required but not provided');
      return [];
    }

    try {
      const recognitions = await this.repo.getRecognitions(schoolId, true);
      return recognitions;
    } catch (error) {
      console.error('Error fetching recognitions:', error);
      throw error;
    }
  }

  /**
   * Subscribe to recognitions changes
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {Function} callback - Callback for updates
   * @returns {Function} Unsubscribe function
   */
  subscribeRecognitions(schoolId, callback) {
    if (!schoolId) {
      console.warn('RecognitionService.subscribeRecognitions: schoolId required but not provided');
      callback([]);
      return () => {};
    }

    // Wrapper callback to filter and sort recognitions
    const wrappedCallback = (recognitions) => {
      if (!recognitions || recognitions.length === 0) {
        callback([]);
        return;
      }

      // Filter out explicitly inactive recognitions (active=false) client-side
      const activeRecognitions = recognitions.filter(r => r.active !== false && r.retired !== true);

      // Sort by sortOrder if exists, otherwise by name
      const sorted = activeRecognitions.sort((a, b) => {
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        return (a.name || '').localeCompare(b.name || '');
      });
      callback(sorted);
    };

    return this.repo.subscribeDefinitions(wrappedCallback, schoolId);
  }

  /**
   * Get recognitions by value
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {string} valueId - Value ID
   * @returns {Promise<Array>} Array of recognitions for this value
   */
  async getRecognitionsByValue(schoolId, valueId) {
    if (!schoolId) {
      console.warn('RecognitionService.getRecognitionsByValue: schoolId required but not provided');
      return [];
    }

    try {
      return await this.repo.getByValueForPeriod(schoolId, valueId, new Date(0), new Date());
    } catch (error) {
      console.error('Error fetching recognitions by value:', error);
      return [];
    }
  }

  /**
   * Update a recognition
   * @param {string} recognitionId - Recognition ID
   * @param {Object} updates - Updates to apply
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<void>}
   */
  async updateRecognition(recognitionId, updates, schoolId) {
    try {
      // Add schoolId to updates for hierarchical collections
      const updatesWithSchool = schoolId ? { ...updates, schoolId } : updates;
      await this.repo.update(recognitionId, updatesWithSchool);
    } catch (error) {
      console.error('Error updating recognition:', error);
      throw error;
    }
  }

  // Alias so RuleEvaluator (which calls mentionService.updateMention) works with recognitions
  async updateMention(recognitionId, updates) {
    return this.updateRecognition(recognitionId, updates, updates?.schoolId);
  }

  /**
   * Delete a recognition
   * @param {string} recognitionId - Recognition ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<void>}
   */
  async deleteRecognition(recognitionId, schoolId) {
    try {
      // Soft delete by setting active=false
      await this.repo.update(recognitionId, { active: false, schoolId });
    } catch (error) {
      console.error('Error deleting recognition:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time recognition updates (issued recognitions)
   * @param {string} schoolId - School ID to filter by
   * @param {Function} callback - Callback for updates
   * @param {number} limitCount - Number of recognitions to fetch
   * @returns {Function} Unsubscribe function
   */
  subscribeToRecognitions(schoolId, callback, limitCount = 30) {
    console.log('[RecognitionService] Setting up real-time subscription:', { schoolId, limitCount });

    if (!schoolId) {
      console.warn('[RecognitionService] No schoolId provided, returning empty');
      callback([]);
      return () => {};
    }

    // Use repository subscribe with hierarchical support
    const constraints = [
      { field: 'active', operator: '==', value: true }
    ];

    return this.repo.subscribeRecognitions(records => callback(records.filter(isIssuedRecognition)), constraints, schoolId);
  }

  /**
   * Get recent recognitions (issued recognitions, not recognition types)
   * @param {number} limitCount - Number of recognitions to fetch
   * @param {string} schoolId - School ID to filter by
   * @returns {Promise<Array>} Array of recent recognitions
   */
  async getRecentRecognitions(limitCount = 30, schoolId = null) {
    if (!schoolId) {
      console.warn('[RecognitionService] schoolId required but not provided');
      return [];
    }

    try {
      console.log('[RecognitionService] Fetching recent recognitions:', { limitCount, schoolId });
      return await this.repo.getRecent(schoolId, limitCount);
    } catch (error) {
      console.error('[RecognitionService] Error fetching recent recognitions:', error);
      return [];
    }
  }

  /**
   * Get recognitions for a specific student
   * @param {string} studentId - Student ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {Object} options - Query options (startDate, endDate, limit, valueId)
   * @returns {Promise<Array>} Array of student recognitions
   */
  async getStudentRecognitions(studentId, schoolId, options = {}) {
    if (!schoolId) {
      console.warn('[RecognitionService] schoolId required but not provided');
      return [];
    }

    try {
      return await this.repo.getByStudentId(studentId, { ...options, schoolId });
    } catch (error) {
      if (error.code === 'permission-denied') {
        const err = new Error('Permission denied reading recognitions');
        err.code = 'PERMISSION_DENIED';
        throw err;
      }
      console.error('[RecognitionService] Error fetching student recognitions:', error);
      return [];
    }
  }

  /**
   * Issue a recognition for a student (similar to issueMention)
   * @param {Object} recognitionData - Recognition details
   * @returns {Promise<Object>} Created recognition
   */
  async issueRecognition(recognitionData) {
    if (!recognitionData.schoolId) {
      console.error('[RecognitionService] Cannot issue recognition: schoolId is required');
      throw new Error('schoolId is required to issue recognition');
    }

    try {
      // Auto-resolve houseId if not provided
      if (!recognitionData.houseId && recognitionData.studentId && recognitionData.schoolId) {
        try {
          const { resolveHouseForStudent } = await import('../utils/houseResolver');
          const resolved = await resolveHouseForStudent(recognitionData.studentId, recognitionData.schoolId);
          recognitionData.houseId = resolved.houseId;
          recognitionData.houseName = resolved.houseName;
        } catch (houseErr) {
          console.warn('[RecognitionService] House resolution failed (non-blocking):', houseErr.message);
        }
      }

      console.log('[RecognitionService] Attempting to save recognition:', recognitionData);

      // Use repository create method which handles hierarchical routing
      const savedRecognition = await this.repo.create(recognitionData);

      console.log('[RecognitionService] Recognition saved successfully:', savedRecognition);

      // Update points if points service is available
      if (pointsService && recognitionData.schoolId) {
        try {
          // Use first value for points (backward compat), or legacy single value
          const primaryValue = recognitionData.values?.[0];
          await pointsService.addPoints({
            studentId: recognitionData.studentId,
            studentName: recognitionData.studentName,
            classId: recognitionData.classId,
            className: recognitionData.className,
            houseId: recognitionData.houseId,
            houseName: recognitionData.houseName,
            amount: recognitionData.points || 1,
            type: 'recognition',
            sourceId: savedRecognition.id,
            valueId: primaryValue?.id || recognitionData.valueId,
            valueName: primaryValue?.name || recognitionData.value || recognitionData.valueName,
            schoolId: recognitionData.schoolId
          });
        } catch (pointsError) {
          console.error('Failed to update points for recognition:', pointsError);
          // Don't fail the recognition if points update fails
        }
      }

      // Credit currency if the recognition domain (or its recognition type) declares one.
      // Fire-and-forget — runs alongside the legacy PointsService.addPoints call above.
      recordAwardForEvent({
        schoolId: recognitionData.schoolId,
        domainId: 'recognition',
        typesCollection: 'recognitionTypes',
        typeId: recognitionData.recognitionTypeId || recognitionData.typeId,
        eventId: savedRecognition.id,
        event: {
          studentId: recognitionData.studentId,
          studentName: recognitionData.studentName,
          className: recognitionData.className,
          issuedById: recognitionData.issuedById,
          issuedByName: recognitionData.issuedByName,
          typeLabel: recognitionData.recognitionType || recognitionData.value,
          points: recognitionData.points,
          issuedAt: savedRecognition.issuedAt || recognitionData.issuedAt,
          createdAt: savedRecognition.createdAt || recognitionData.createdAt,
        },
      }).catch(err => console.error('[RecognitionService] currency award failed:', err.message));

      // Process recognition rules (badges, achievements, etc.)
      // Wrapped in try/catch to not break existing recognition creation
      try {
        await recognitionRuleEvaluator.processRulesForMention(savedRecognition);

        // Invalidate cache after rule processing (if StudentDataFetcher exists)
        if (recognitionRuleEvaluator.studentDataFetcher) {
          recognitionRuleEvaluator.studentDataFetcher.invalidateCache(savedRecognition.studentId);
        }

        console.log('[RecognitionService] Rules processed successfully for recognition:', savedRecognition.id);
      } catch (error) {
        console.error('[RecognitionService] Error processing recognition rules:', error);
        // Don't fail recognition creation if rules fail
      }

      return savedRecognition;
    } catch (error) {
      console.error('Error issuing recognition:', error);
      throw error;
    }
  }

}

// Create service instances
const pointsRepository = new PointsRepository();
export const pointsService = new PointsService(pointsRepository);
export const recognitionService = new RecognitionService();

// Initialize domain-specific repositories
const recognitionRuleRepo = new RecognitionRuleRepository();
const achievementRepo = new AchievementRepository();

// Create service instances (REUSING existing classes with domain repos)
export const recognitionRuleService = new RuleService(recognitionRuleRepo);
export const achievementService = new ConsequenceService(achievementRepo);

// Create rule evaluator (REUSING existing RuleEvaluator class)
export const recognitionRuleEvaluator = new RuleEvaluator({
  mentionService: recognitionService,        // Parameter name is "mentionService" but we pass recognitionService
  consequenceService: achievementService,    // Parameter name is "consequenceService" but we pass achievementService
  ruleService: recognitionRuleService,
  domainId: 'recognition'                    // Count recognition events (not discipline) for threshold/count rules
});

// Wire circular dependency
achievementService.setRuleEvaluator(recognitionRuleEvaluator);

// Convenience exports (mirrors disciplineService pattern)
export const subscribeRecognitionRules = (...args) => recognitionRuleService.subscribeRules(...args);
export const updateRecognitionRuleSortOrders = (...args) => recognitionRuleService.updateSortOrders(...args);

export default recognitionService;
